import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from 'styles/theme/theme';

export type FillGradientDirection = 'right' | 'left' | 'up' | 'down';

/**
 * Mirror of the editor core's gradient config (`fill-gradient-plugin.ts`). Defined
 * locally rather than imported so the manager UI stays decoupled from the editor's
 * internal types and the `fabric-editor` public surface stays minimal — the same
 * convention `corner-radii-strategies.ts` uses with `PlaceholderCornerRadii`. The
 * shape is structural, so values flow into `editor.fillGradient.set` unchanged.
 *
 * A vignette is not a separate thing here: it is this config with the end stop at
 * `endAlpha: 0` and `endColor === color` — which is exactly what `seedOf` produces,
 * so switching the section on reproduces the old fade-to-transparent behaviour.
 */
export interface FillGradientConfig {
  color: string;
  alpha: number;
  endColor: string;
  endAlpha: number;
  direction: FillGradientDirection;
  /** Where the transition to the end stop begins, 0..1 along the gradient (0.5 = from the middle). */
  fadeStart: number;
}

const DEFAULT_DIRECTION: FillGradientDirection = 'right';
const DEFAULT_FADE_START = 0.5;
const DEFAULT_COLOR = theme.colors.DarkBlue200;

interface Aggregate<T> {
  value: T | undefined;
  isMixed: boolean;
}

export interface FillGradientControl {
  enabled: Aggregate<boolean>;
  color: Aggregate<string>;
  alpha: Aggregate<number>;
  endColor: Aggregate<string>;
  endAlpha: Aggregate<number>;
  direction: Aggregate<FillGradientDirection>;
  fadeStart: Aggregate<number>;
  setEnabled: (on: boolean) => void;
  /** Partial write across every selected object; a no-op while the gradient is off. */
  patch: (changes: Partial<FillGradientConfig>) => void;
}

function aggregate<T>(objects: fabric.Object[], read: (o: fabric.Object) => T | undefined): Aggregate<T> {
  if (objects.length === 0) return { value: undefined, isMixed: false };
  const first = read(objects[0]);
  for (let i = 1; i < objects.length; i++) {
    if (read(objects[i]) !== first) return { value: undefined, isMixed: true };
  }
  return { value: first, isMixed: false };
}

/**
 * Reads/writes the gradient fill across one or more selected objects. Each field is
 * aggregated independently (so a multi-selection that agrees on, say, direction but
 * differs on color reports only color as mixed). Reads go through
 * `editor.fillGradient.get`, which transparently upgrades a legacy `data.vignette`,
 * so this hook never sees the old shape. Writes route through
 * `editor.fillGradient.set`, which rebuilds the gradient `fill` from the config and
 * records one undo step per call; the hook re-reads on `object:modified` so an undo
 * or an external change keeps the inspector in sync.
 */
export function useFillGradient(objects: fabric.Object[], editor: FabricEditor): FillGradientControl {
  const objectSet = useMemo(() => new Set(objects), [objects]);

  const configOf = useCallback(
    (o: fabric.Object): FillGradientConfig | undefined => editor.fillGradient?.get(o) as FillGradientConfig | undefined,
    [editor],
  );

  /** Existing config, or a default one seeded from the object's current solid fill —
   * a fade of that color to transparent, i.e. the former "vignette" default. */
  const seedOf = useCallback(
    (o: fabric.Object): FillGradientConfig => {
      const existing = configOf(o);
      if (existing) return existing;
      const base = typeof o.fill === 'string' ? o.fill : DEFAULT_COLOR;
      return {
        color: base,
        alpha: 1,
        endColor: base,
        endAlpha: 0,
        direction: DEFAULT_DIRECTION,
        fadeStart: DEFAULT_FADE_START,
      };
    },
    [configOf],
  );

  const read = useCallback(
    (): Omit<FillGradientControl, 'setEnabled' | 'patch'> => ({
      enabled: aggregate(objects, (o) => Boolean(configOf(o))),
      color: aggregate(objects, (o) => configOf(o)?.color),
      alpha: aggregate(objects, (o) => configOf(o)?.alpha),
      endColor: aggregate(objects, (o) => configOf(o)?.endColor),
      endAlpha: aggregate(objects, (o) => configOf(o)?.endAlpha),
      direction: aggregate(objects, (o) => configOf(o)?.direction),
      fadeStart: aggregate(objects, (o) => configOf(o)?.fadeStart),
    }),
    [objects, configOf],
  );

  const [state, setState] = useState(read);

  useEffect(() => {
    setState(read());
    const off = editor.on('object:modified', (e) => {
      if (objectSet.has(e.target as fabric.Object)) setState(read());
    });
    return () => off();
  }, [editor, objectSet, read]);

  const setEnabled = useCallback(
    (on: boolean) => {
      for (const o of objects) {
        if (on === Boolean(configOf(o))) continue; // already in target state
        editor.fillGradient?.set(o, on ? seedOf(o) : undefined);
      }
      setState(read());
    },
    [objects, editor, read, configOf, seedOf],
  );

  const patch = useCallback(
    (changes: Partial<FillGradientConfig>) => {
      for (const o of objects) {
        if (!configOf(o)) continue; // only meaningful while the gradient is on
        editor.fillGradient?.set(o, { ...seedOf(o), ...changes });
      }
      setState(read());
    },
    [objects, editor, read, configOf, seedOf],
  );

  return { ...state, setEnabled, patch };
}
